import { Box } from '@/components/sgBootstrapReactWrapper';
import type { Root } from '@/model/Root';
import { usePreserveQueryParams } from '@/store/hooks';
import { isNotDefined } from '@/utils/assertions';
import { Link } from '@tanstack/react-router';
import type { ColDef } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { useMemo } from 'react';
import { FormattedMessage, type IntlFormatters, useIntl } from 'react-intl';
import { AGGridWrapper } from '../common/AGGridWrapper';
import { RootCard } from './RootCard';
import RootStatusBadge from './RootStatusBadge';
import RootTypeBadge from './RootTypeBadge';

export type RootsListProps = {
  // list : display all elements as a list of cards, will only display first 10 of them
  // table: will use aggrid to display results
  displayMode: 'list' | 'table';
  roots: Root[];
  emphasizedText?: string;
};

const NB_DISPLAYED_RESULTS__LISTMODE = 8;

export const RootsList = ({ displayMode, roots, emphasizedText = '' }: RootsListProps) => {
  const { $t } = useIntl();
  const { preserveQueryParamsAndNavigate } = usePreserveQueryParams();

  const columnDefs = useMemo(() => generateColumnDefinitions($t), [$t]);

  const displayedRoots = useMemo(
    () => (displayMode === 'table' ? roots : roots.slice(0, NB_DISPLAYED_RESULTS__LISTMODE)),
    [displayMode, roots],
  );

  if (displayedRoots.length === 0) {
    return (
      <div className="px-16px py-12px">
        <FormattedMessage id="noRootFound" />
      </div>
    );
  }

  if (displayMode === 'list') {
    return (
      <div className="container-fluid p-0">
        {displayedRoots.map((root) => {
          return (
            <Link
              key={root.rootId}
              to="/root/$id"
              search={(prev: any) => ({ ...prev })}
              params={{ id: root.rootId }}
              className="text-decoration-none"
            >
              <RootCard root={root} emphasizedText={emphasizedText} />
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <Box flex="min">
      <AGGridWrapper<Root>
        identifier="root-search-results"
        columnDefs={columnDefs}
        rowData={displayedRoots}
        loading={false}
        onRowClicked={({ data }) => {
          if (isNotDefined(data?.rootId)) {
            return;
          }
          preserveQueryParamsAndNavigate({ pathname: `/root/${data.rootId}` });
        }}
        gridHeigth={600}
      />
    </Box>
  );
};

function generateColumnDefinitions($t: IntlFormatters['$t']): ColDef<Root>[] {
  return [
    {
      field: 'rootId',
      headerName: $t({ id: 'roots.table.rootId' }),
      headerTooltip: $t({ id: 'roots.table.rootId' }),
      minWidth: 120,
      cellClass: 'border-end',
    },
    {
      field: 'shortName',
      headerName: $t({ id: 'roots.table.shortName' }),
      headerTooltip: $t({ id: 'roots.table.shortName' }),
      minWidth: 200,
      // filter: 'agTextColumnFilter',
      // filterParams: {
      //   buttons: ['reset', 'apply'],
      //   closeOnApply: true,
      //   maxNumConditions: 1,
      // },
      cellClass: 'border-end',
    },
    {
      field: 'legalName',
      headerName: $t({ id: 'roots.table.legalName' }),
      headerTooltip: $t({ id: 'roots.table.legalName' }),
      minWidth: 220,
      cellClass: 'border-end',
    },
    {
      field: 'rootType',
      headerName: $t({ id: 'roots.table.rootType' }),
      headerTooltip: $t({ id: 'roots.table.rootType' }),
      cellClass: 'border-end',
      minWidth: 180,

      cellRenderer: ({ value }: CustomCellRendererProps) => {
        return <RootTypeBadge rootType={value} />;
      },
    },
    {
      field: 'rootStatus',
      headerName: $t({ id: 'roots.table.rootStatus' }),
      headerTooltip: $t({ id: 'roots.table.rootStatus' }),
      minWidth: 150,
      cellRenderer: ({ value }: CustomCellRendererProps) => {
        return <RootStatusBadge rootStatus={value} />;
      },
      cellClass: 'border-end',
    },
  ];
}
